/** Levels: four cards, locked or unlocked, with the block's exercises, where the
 *  running builds to, and the checkpoint criteria. Section 7. */
import type { StoreApi } from './useStore.ts';
import { Banner } from './components.tsx';
import { PLAN, META } from '../plan/data.ts';
import { unlockedLevel, critMet, gatePassed } from '../plan/phase.ts';
import { bestMeanUpTo } from '../plan/weights.ts';
import { TickBar } from './components.tsx';
import { VideoLink } from './VideoLink.tsx';
import type { TickState } from './components.tsx';

export function Levels({ api }: { api: StoreApi }) {
  const { state, durable } = api;
  const startDate = state.get<string>('setting:startDate') ?? META.defaultStartDate;
  const unlocked = unlockedLevel(state, startDate);

  return (
    <>
      <h1>Levels</h1>
      <p className="sub">Every fourth week ends in a test that unlocks the next block.</p>

      {PLAN.gates.map((gate) => {
        const isUnlocked = gate.lvl <= unlocked;
        const passed = gatePassed(state, startDate, gate);
        const block = PLAN.lifts[String(gate.lvl)];
        const runs = PLAN.runs[String(gate.week)];
        const best = bestMeanUpTo(state, startDate, gate.week);

        return (
          <section key={gate.lvl}>
            <h2>
              {gate.name} {isUnlocked
                ? <span className="ok">· unlocked</span>
                : <span className="dim">· locked</span>}
            </h2>
            <div className="card">
              <p className="focus" style={{ marginTop: 0 }}>{gate.why}</p>
              <div className="row">
                <span className="k">Opens</span><span className="v">{gate.opens}</span>
              </div>
              <div className="row">
                <span className="k">Tested at end of</span><span className="v">Week {gate.week}</span>
              </div>
              <div className="row">
                <span className="k">Running builds to</span>
                <span className="v">{longestBlockLabel(runs)}</span>
              </div>
            </div>

            <div className="card">
              <div className="ex-name" style={{ marginBottom: 6 }}>Checkpoint criteria</div>
              {gate.crit.map((c, i) => {
                const key = `crit:${gate.lvl}:${i}`;
                const met = critMet(state, startDate, gate, i);
                if (c.auto !== undefined) {
                  return (
                    <div className="autocrit" key={i}>
                      <span className={`tickbox tick-${met ? 'on' : 'off'}`} aria-hidden="true">
                        {met ? '✓' : ''}
                      </span>
                      <span>
                        <div>{c.t}</div>
                        <div className="tickbar-sub">
                          Automatic — read from your 7-day mean, never a single weigh-in.
                          {' '}Best so far: {best === null ? 'unknown, not enough weigh-ins' : `${best.toFixed(2)} kg`}
                          {' '}· needs ≤ {c.auto} kg
                        </div>
                      </span>
                    </div>
                  );
                }
                const on = state.get(key) === 1;
                const ts: TickState = api.pending.has(key) ? 'writing'
                  : api.errorFor(key) ? 'failed' : on ? 'on' : 'off';
                return (
                  <div key={i}>
                    <TickBar state={ts} disabled={!durable} title={c.t}
                             onToggle={() => void api.setFlag(key, !on)} />
                    {api.errorFor(key) && <div className="errline">Not saved — {api.errorFor(key)}</div>}
                  </div>
                );
              })}
              {passed
                ? <Banner kind="ok">Every criterion met. {gate.opens} is open.</Banner>
                : <p className="sub" style={{ marginBottom: 0 }}>
                    Outstanding criteria hold you at the previous block for at most two weeks.
                  </p>}
            </div>

            <div className="card">
              <div className="ex-name" style={{ marginBottom: 6 }}>Block {gate.lvl} lifting</div>
              {(['A', 'B'] as const).map((s) => (
                <details key={s} className="wfood">
                  <summary>
                    <span>{block[s].name}</span>
                    <span className="dim">{block[s].ex.length} exercises</span>
                  </summary>
                  {block[s].ex.map((e, i) => (
                    <div className="exrow" key={i}>
                      <div className="row" style={{ borderBottom: 'none', paddingBottom: 2 }}>
                        <span className="k">{e.n}</span>
                        <span className="v">{e.s} × {e.r}</span>
                      </div>
                      <VideoLink api={api} ex={e} />
                    </div>
                  ))}
                </details>
              ))}
            </div>
          </section>
        );
      })}
    </>
  );
}

function longestBlockLabel(runs: typeof PLAN.runs[string]): string {
  const longest = Math.max(...runs.flatMap((r) => r.blocks.map((b) => b.min)));
  const test = runs[2]?.title ?? '';
  return `${longest} min continuous${/TEST/i.test(JSON.stringify(runs[2])) ? ` · ${test}` : ''}`;
}
